import { Badge } from '@/components/ui/badge';
import { GripVertical, X, Edit3 } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface TimelineEventCardProps {
  procedure: LocalScenarioProcedure;
  index: number;
  onRemove: () => void;
  onEdit: () => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetPhase: string) => void;
  compact?: boolean;
}

export function TimelineEventCard({
  procedure,
  index,
  onRemove,
  onEdit,
  onDragStart,
  onDragOver,
  onDrop,
  compact,
}: TimelineEventCardProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const totalCost = procedure.estimatedCost * procedure.quantity;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, procedure.phase)}
      className={`group relative p-2.5 rounded-lg border transition-all cursor-grab active:cursor-grabbing hover:shadow-sm ${
        ahpDark
          ? 'border-civic-gold/20 bg-civic-card-dark hover:border-civic-gold/40'
          : isAhp
          ? 'border-gray-200 bg-white hover:border-civic-money/40'
          : 'border-white/10 bg-black/30 hover:border-matrix-green/30'
      }`}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="w-3.5 h-3.5 mt-0.5 text-muted-foreground/40 shrink-0 group-hover:text-muted-foreground" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Badge
              variant="outline"
              className={`text-[9px] px-1 py-0 leading-tight ${
                isAhp ? '' : 'border-matrix-green/20 text-matrix-green'
              }`}
            >
              {procedure.codeType}
            </Badge>
            <span className={`font-mono text-[10px] font-semibold ${isAhp ? 'text-civic-money' : 'text-matrix-green'}`}>
              {procedure.code}
            </span>
          </div>

          <p className={`text-xs leading-tight ${compact ? 'line-clamp-1' : 'line-clamp-2'} ${
            ahpDark ? 'text-gray-300' : isAhp ? 'text-gray-700' : 'text-foreground/80'
          }`}>
            {procedure.description}
          </p>

          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
            {procedure.quantity > 1 && <span>x{procedure.quantity}</span>}
            <span className={`font-mono font-medium ${ahpDark ? 'text-gray-100' : isAhp ? 'text-gray-900' : 'text-foreground'}`}>
              ${totalCost.toLocaleString()}
            </span>
            {procedure.setting && procedure.setting !== 'unspecified' && (
              <span className="capitalize opacity-60">{procedure.setting}</span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className={`p-1 rounded transition-colors ${
              isAhp ? 'hover:bg-gray-100' : 'hover:bg-white/10'
            }`}
          >
            <Edit3 className="w-3 h-3 text-muted-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 rounded transition-colors hover:bg-destructive/20 hover:text-destructive"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
